import { useRef, useEffect } from "react";
import PropTypes from 'prop-types';

import "@kitware/vtk.js/Rendering/Profiles/Geometry";
// Load the rendering pieces we want to use (for both WebGL and WebGPU)
import "@kitware/vtk.js/Rendering/Profiles/All";

// Force DataAccessHelper to have access to various data source
import "@kitware/vtk.js/IO/Core/DataAccessHelper/HtmlDataAccessHelper";
import "@kitware/vtk.js/IO/Core/DataAccessHelper/HttpDataAccessHelper";
import "@kitware/vtk.js/IO/Core/DataAccessHelper/JSZipDataAccessHelper";

import macro from "@kitware/vtk.js/macros";
import DataAccessHelper from "@kitware/vtk.js/IO/Core/DataAccessHelper";
import HttpDataAccessHelper from "@kitware/vtk.js/IO/Core/DataAccessHelper/HttpDataAccessHelper";
import vtkHttpSceneLoader from "@kitware/vtk.js/IO/Core/HttpSceneLoader";
import vtkRenderWindow from "@kitware/vtk.js/Rendering/Core/RenderWindow";
import vtkRenderer from "@kitware/vtk.js/Rendering/Core/Renderer";
import vtkOpenGLRenderWindow from "@kitware/vtk.js/Rendering/OpenGL/RenderWindow";
import vtkRenderWindowInteractor from "@kitware/vtk.js/Rendering/Core/RenderWindowInteractor";
import vtkInteractorStyleTrackballCamera from "@kitware/vtk.js/Interaction/Style/InteractorStyleTrackballCamera";

import { Container, Stack } from "react-bootstrap";
import { Button, ButtonGroup } from "react-bootstrap";
import { apiFetch } from "../api";

function emptyContainer(container) {
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }

const VTKScene = ({ url, showButtons, viewWidth }) => {
    const vtkContainerRef = useRef(null);
    const context = useRef(null);

    useEffect(() => {
        if (!context.current && url) {
            try {
                const openglRenderWindow = vtkOpenGLRenderWindow.newInstance();
                const renderWindow = vtkRenderWindow.newInstance();
                const renderer = vtkRenderer.newInstance({
                    background: [0, 0, 0],
                    setEnvironmentTextureDiffuseStrength: 0.99005,
                });
                renderWindow.addRenderer(renderer);

                apiFetch(url)
                    .then((res) => {
                        if (!res.ok) {
                            throw new Error(`VTK download failed: ${res.status}`);
                        }
                        return res.arrayBuffer();
                    })
                    .then((zipBuffer) => {
                        const zipContent = new Uint8Array(zipBuffer);
                        const dataAccessHelper = DataAccessHelper.get("zip", {
                            zipContent,
                            callback: (zip) => {
                                const sceneImporter = vtkHttpSceneLoader.newInstance({
                                    renderer,
                                    dataAccessHelper,
                                });
                                sceneImporter.setUrl("index.json");
                                sceneImporter.onReady(() => {
                                    renderWindow.render();

                                    renderWindow.addView(openglRenderWindow);
                                    openglRenderWindow.setSize(viewWidth, viewWidth);
                                    openglRenderWindow.setContainer(vtkContainerRef.current);

                                    const interactor = vtkRenderWindowInteractor.newInstance();
                                    interactor.setView(openglRenderWindow);
                                    interactor.initialize();
                                    interactor.bindEvents(vtkContainerRef.current);
                                    interactor.setInteractorStyle(
                                        vtkInteractorStyleTrackballCamera.newInstance()
                                    );

                                    renderer.resetCamera();
                                    renderWindow.render();

                                    // Store all VTK objects in context for cleanup
                                    context.current = {
                                        openglRenderWindow,
                                        renderWindow,
                                        renderer,
                                        interactor,
                                        sceneImporter,
                                        dataAccessHelper
                                    };
                                });
                            },
                        });
                    })
                    .catch(error => {
                        console.error('Failed to load VTK scene:', error);
                        // You might want to show an error message to the user here
                    });
            } catch (error) {
                console.error('Failed to initialize VTK:', error);
                // You might want to show an error message to the user here
            }
        }

        return () => {
            if (context.current) {
                const { 
                    openglRenderWindow, 
                    renderWindow, 
                    renderer, 
                    interactor,
                    sceneImporter
                } = context.current;
                
                // Clean up in reverse order of creation
                if (interactor) {
                    interactor.delete();
                }
                if (sceneImporter) {
                    sceneImporter.delete();
                }
                if (renderer) {
                    renderer.delete();
                }
                if (renderWindow) {
                    renderWindow.delete();
                }
                if (openglRenderWindow) {
                    openglRenderWindow.delete();
                }
                context.current = null;
            }
        };
    }, [url, viewWidth]);

    function resetCamera() {
        if (context.current) {
            context.current.renderer.resetCamera();
            context.current.renderWindow.render();
        }
    }

    function saveFrame() {
        context.current.renderWindow.captureImages("image/png")[0].then((image) => {
            const downloadA = document.getElementById("imageDownload");
            downloadA.setAttribute("href", image);
            downloadA.click();
        });
    }

    return (
        <Container fluid id="viewer">
            <Stack direction="vertical" gap={3}>
                <Container
                    id="VTKContainer"
                    className="bg-light me-auto"
                    style={{ width: viewWidth, height: "auto" }}
                    ref={vtkContainerRef}
                ></Container>
                {showButtons && (
                    <Stack className="bg-light border">
                        <ButtonGroup className="my-2">
                            <Button variant="outline-primary" onClick={resetCamera}>
                                Reset Camera
                            </Button>
                            <a
                                id="imageDownload"
                                href=""
                                download="download_scene"
                                style={{ visibility: "hidden" }}
                            >
                                nil
                            </a>
                            <Button variant="outline-primary" onClick={saveFrame}>
                                Save Image
                            </Button>
                        </ButtonGroup>
                    </Stack>
                )}
            </Stack>
        </Container>
    );
};

VTKScene.propTypes = {
    url: PropTypes.string.isRequired,
    showButtons: PropTypes.bool,
    viewWidth: PropTypes.number.isRequired
};

VTKScene.defaultProps = {
    showButtons: true
};

export default VTKScene;
